import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEOHead, TopNavbar, Footer } from "@components/organisms";
import { QRTicketViewer } from "@components/organisms/QRTicketViewer";
import { useAuth } from "@app/providers/useAuth";
import { useEvents } from "@modules/events/hooks";
import { TicketEventCard } from "@modules/tickets/components";
import { ticketService } from "@modules/tickets/services/ticket.service";
import { ROUTES } from "@shared/constants";
import { formatDate, logger } from "@shared/utils";
import type { Ticket } from "@/types";

/**
 * My Tickets page.
 * Shows upcoming and past purchased events with real QR codes.
 * Clicking an upcoming event opens the QR viewer.
 */
export function MyTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: events } = useEvents();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch tickets when user email is available
  useEffect(() => {
    const fetchTickets = async () => {
      if (!user?.mail) {
        setLoading(false);
        return;
      }

      try {
        const response = await ticketService.getMyTickets(user.mail);
        if (response.success) {
          setTickets(response.data);
        }
      } catch (error) {
        logger.error("Error fetching tickets:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [user?.mail]);

  // Get event details from events list
  const getEventDetails = (eventId: string) => {
    return events?.find((ev) => String(ev.id) === eventId);
  };

  // Separate into upcoming/past by CALENDAR DAY (not datetime).
  // Priority: ticket.eventDateIso (parsed from saleTemp.date) > local event list date.
  // Local dates with year < 2020 are considered corrupted backend data → treat as upcoming.
  // bug-20260619: comparar contra la HORA actual marcaba los eventos de HOY como
  // "pasados" apenas pasaba la medianoche (eventDateIso queda a las 00:00). Un evento
  // de hoy debe seguir en "Próximos" hasta que termine el día.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const resolveEventDate = (ticket: Ticket): Date | null => {
    if (ticket.eventDateIso) return new Date(ticket.eventDateIso);
    const event = getEventDetails(ticket.eventId);
    if (!event?.dateevent) return null;
    const d = new Date(event.dateevent);
    if (d.getFullYear() < 2020) return null; // corrupted data — ignore
    return d;
  };

  // Normaliza al inicio del día local para comparar por día calendario.
  const startOfDay = (d: Date): Date => {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
  };

  const upcomingTickets = tickets.filter((ticket) => {
    const date = resolveEventDate(ticket);
    return date === null || startOfDay(date) >= startOfToday; // hoy o futuro
  });

  const pastTickets = tickets.filter((ticket) => {
    const date = resolveEventDate(ticket);
    return date !== null && startOfDay(date) < startOfToday; // estrictamente antes de hoy
  });

  // Map ticket to Event shape
  const ticketToEventDisplay = (ticket: Ticket) => {
    const event = getEventDetails(ticket.eventId);
    // For the date: prefer eventDateIso (parsed from saleTemp.date), then local event date
    // if valid (year >= 2020), otherwise fall back to now (card still shows, just no date).
    const localDate = event?.dateevent;
    const localDateValid =
      localDate && new Date(localDate).getFullYear() >= 2020 ? localDate : null;
    return {
      id: ticket.id,
      name: event?.name ?? ticket.eventName ?? ticket.code,
      image: event?.image ?? ticket.image ?? null,
      dateevent: ticket.eventDateIso ?? localDateValid ?? new Date().toISOString(),
      slug: null,
      location: {
        city: "",
        state: "",
        venue: "",
        addressLine: "",
        country: "",
      },
      category: "conciertos" as const,
      price: 0,
      description: null,
      accessibility: null,
      duration: null,
      hour: null,
      ageRestriction: null,
      favorite: false,
      iva: 0,
      state: 0,
      visible: 1,
      maxventadigital: 0,
      cedulaporboleto: 0,
      address: null,
      summary: null,
      tipoEvento: 0,
    };
  };

  return (
    <>
      <SEOHead title="Mis Tickets" noIndex />
      <div className="flex min-h-screen flex-col bg-gradient-to-b from-neutral-60 to-primary-110">
        <TopNavbar events={events ?? []} opaque />

        <main className="flex flex-1 flex-col gap-6 px-4 sm:px-8 md:px-16 lg:px-30 py-10">
          <div className="flex items-center gap-3">
            <button
              className="cursor-pointer border-none bg-transparent text-white"
              onClick={() => navigate(ROUTES.HOME)}
              aria-label="Volver"
            >
              <ArrowLeft size={26} />
            </button>
            <span className="font-semibold text-white" style={{ fontSize: 20 }}>
              Mis Tickets
            </span>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center gap-4 py-10">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-secondary-120 border-t-white" />
              <p className="text-neutral-80" style={{ fontSize: 14 }}>
                Cargando tickets...
              </p>
            </div>
          )}

          {!loading && tickets.length === 0 && (
            <div className="py-10 text-center">
              <p className="text-neutral-80" style={{ fontSize: 14 }}>
                No tienes tickets comprados
              </p>
            </div>
          )}

          {!loading && tickets.length > 0 && (
            <>
              <span
                className="font-semibold text-secondary-120"
                style={{ fontSize: 14 }}
              >
                Próximos eventos
              </span>
              <div className="flex flex-col gap-3">
                {upcomingTickets.length > 0 ? (
                  upcomingTickets.map((ticket) => {
                    const eventDisplay = ticketToEventDisplay(ticket);
                    return (
                      <TicketEventCard
                        key={ticket.id}
                        event={eventDisplay}
                        showArrow
                        onClick={() => setSelectedTicket(ticket)}
                      />
                    );
                  })
                ) : (
                  <p className="text-neutral-80" style={{ fontSize: 14 }}>
                    No tienes próximos eventos
                  </p>
                )}
              </div>

              <span
                className="font-semibold text-secondary-120"
                style={{ fontSize: 14 }}
              >
                Eventos pasados
              </span>
              <div className="flex flex-col gap-3">
                {pastTickets.length > 0 ? (
                  pastTickets.map((ticket) => {
                    const eventDisplay = ticketToEventDisplay(ticket);
                    return (
                      <TicketEventCard key={ticket.id} event={eventDisplay} />
                    );
                  })
                ) : (
                  <p className="text-neutral-80" style={{ fontSize: 14 }}>
                    No tienes eventos pasados
                  </p>
                )}
              </div>
            </>
          )}
        </main>

        <Footer />

        {selectedTicket && (
          <QRTicketViewer
            isOpen={!!selectedTicket}
            onClose={() => setSelectedTicket(null)}
            eventName={
              getEventDetails(selectedTicket.eventId)?.name ??
              selectedTicket.eventName ??
              selectedTicket.code
            }
            eventImage={
              getEventDetails(selectedTicket.eventId)?.image ??
              selectedTicket.image ??
              null
            }
            localityName={selectedTicket.localityName}
            ticketCount={1}
            eventDate={formatDate(
              selectedTicket.eventDateIso ??
              (() => {
                const d = getEventDetails(selectedTicket.eventId)?.dateevent;
                return d && new Date(d).getFullYear() >= 2020 ? d : "";
              })()
            )}
            ticketCode={selectedTicket.code}
            qrSrc={selectedTicket.qr}
          />
        )}
      </div>
    </>
  );
}
